export const ANALYTICS_SCHEMA_VERSION = 2

export type AnalyticsPrimitive = string | number | boolean | null
export type AnalyticsProperties = Record<string, AnalyticsPrimitive>

export type AnalyticsAttributionTouch = {
  source: string
  medium: string
  campaign: string | null
  term: string | null
  content: string | null
  landing_path: string
  referrer_host: string | null
  has_gclid: boolean
  has_msclkid: boolean
  has_fbclid: boolean
  captured_at: string
}

export type AnalyticsClientContext = {
  visitorId: string
  sessionId: string
  firstTouch: AnalyticsAttributionTouch
  lastNonDirectTouch: AnalyticsAttributionTouch | null
}

export type AnalyticsRequestContext = AnalyticsClientContext & {
  googleClientId: string | null
  googleSessionId: string | null
  googleEngagementTimeMsec: number
  amplitudeSessionId: number | null
}

const FORBIDDEN_ANALYTICS_PROPERTIES = new Set([
  'answer',
  'cancellation_note',
  'content',
  'email',
  'error_message',
  'feedback_note',
  'name',
  'query',
  'question',
  'stack',
  'user_email',
  'user_name',
])

export function assertAnalyticsEvent(
  eventName: string,
  properties: Record<string, unknown>,
): AnalyticsProperties {
  normalizeAnalyticsEventName(eventName)

  for (const [key, value] of Object.entries(properties)) {
    if (FORBIDDEN_ANALYTICS_PROPERTIES.has(key)) {
      throw new Error(`Analytics property "${key}" is forbidden`)
    }
    if (
      value === undefined ||
      (typeof value !== 'string' &&
        typeof value !== 'number' &&
        typeof value !== 'boolean' &&
        value !== null)
    ) {
      throw new Error(`Analytics property "${key}" must be a primitive value`)
    }
  }

  return properties as AnalyticsProperties
}

export function normalizeAnalyticsEventName(eventName: string) {
  const normalized = eventName
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

  const eventWithLeadingLetter = /^[a-z]/.test(normalized)
    ? normalized
    : `event_${normalized || 'interaction'}`

  return eventWithLeadingLetter.slice(0, 40).replace(/_+$/g, '')
}
